import { useEffect, useRef, useState, useCallback } from 'react';
import { useQuery } from '@tanstack/react-query';
import { queryClient, getApiBaseUrl } from '@/lib/queryClient';

interface NotificationData {
  id: number;
  title: string;
  message: string;
  category: 'admin' | 'employee';
  notificationType: string;
  createdAt: string;
  isRead: boolean;
}

interface WebSocketMessage {
  type: 'notification' | 'auth_success' | 'connection';
  data: any;
}

// Feature flag to completely disable WebSocket if it's causing issues
// Safely check process.env (webpack DefinePlugin replaces this at build time)
const WEBSOCKET_ENABLED = (function() {
  try {
    // @ts-ignore - process.env is replaced at build time by webpack DefinePlugin
    return typeof process !== 'undefined' && process.env && process.env.WEBSOCKET_ENABLED !== 'false';
  } catch {
    return true; // Default to enabled if process is not available
  }
})();

export function useWebSocket() {
  const wsRef = useRef<WebSocket | null>(null);
  const [isConnected, setIsConnected] = useState(false);
  const [connectionStatus, setConnectionStatus] = useState<'connecting' | 'connected' | 'disconnected' | 'error'>('disconnected');
  const reconnectTimeoutRef = useRef<NodeJS.Timeout>();
  const isConnectingRef = useRef(false); // Prevent multiple simultaneous connection attempts

  // Avoid calling /auth/me on public pages or before SSO completes
  const params = new URLSearchParams(window.location.search);
  const isGoogleCallback = params.get('google_auth') === 'success';
  const path = window.location.pathname;
  // IMPORTANT: '/' is NOT a public route - it's the dashboard route
  // We need to check session for '/' because it could be the employee dashboard
  const isPublicRoute = (
    path === '/login' ||
    path === '/forgot-password' ||
    path.startsWith('/reset-password') ||
    path.startsWith('/create-password')
  );
  const shouldCheckSession = isGoogleCallback || !isPublicRoute;

  // Get current user data
  const { data: user } = useQuery({
    queryKey: ['/auth/me'],
    retry: false,
    enabled: shouldCheckSession,
  }) as { data?: { user?: { id: string; role: string } } };

  // If WebSocket is disabled, return early with safe defaults (after hooks)
  if (!WEBSOCKET_ENABLED) {
    return {
      isConnected: false,
      connectionStatus: 'disconnected' as const,
      connect: () => {},
      disconnect: () => {},
    };
  }

  const connect = useCallback(() => {
    // Prevent multiple simultaneous connection attempts
    if (isConnectingRef.current || wsRef.current?.readyState === WebSocket.OPEN || wsRef.current?.readyState === WebSocket.CONNECTING) {
      return;
    }

    if (!user?.user?.id) {
      return;
    }

    // Wrap everything in try-catch to prevent any errors from propagating
    try {
      isConnectingRef.current = true;
      
      // Silently fail - don't log or set status to avoid any UI impact
      try {
        // Create WebSocket connection
        // Use API base URL if available, otherwise fall back to current host
        let apiBaseUrl: string | undefined;
        try {
          apiBaseUrl = getApiBaseUrl();
        } catch (e) {
          // Silently fail
        }
        
        let wsUrl: string;
        
        if (apiBaseUrl) {
          try {
            const apiUrl = new URL(apiBaseUrl);
            const wsProtocol = apiUrl.protocol === 'https:' ? 'wss:' : 'ws:';
            const wsHost = apiUrl.host;
            wsUrl = `${wsProtocol}//${wsHost}/ws/notifications`;
            
            // Log WebSocket URL construction for debugging (only in development/staging)
            // @ts-ignore - process.env.NODE_ENV is replaced at build time
            const isDev = typeof process !== 'undefined' && process.env && process.env.NODE_ENV !== 'production';
            if (isDev) {
              console.log('🔌 WebSocket URL constructed:', {
                apiBaseUrl,
                extractedHost: wsHost,
                protocol: wsProtocol,
                finalUrl: wsUrl,
                currentHost: window.location.host
              });
            }
          } catch (e) {
            const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
            wsUrl = `${protocol}//${window.location.host}/ws/notifications`;
            // @ts-ignore
            const isDev = typeof process !== 'undefined' && process.env && process.env.NODE_ENV !== 'production';
            if (isDev) {
              console.warn('⚠️ Failed to parse API base URL, using window.location.host:', wsUrl);
            }
          }
        } else {
          const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
          wsUrl = `${protocol}//${window.location.host}/ws/notifications`;
          // @ts-ignore
          const isDev = typeof process !== 'undefined' && process.env && process.env.NODE_ENV !== 'production';
          if (isDev) {
            console.warn('⚠️ No API base URL, using window.location.host:', wsUrl);
          }
        }
        
        // Create WebSocket with error suppression
        let ws: WebSocket;
        try {
          // @ts-ignore
          const isDev = typeof process !== 'undefined' && process.env && process.env.NODE_ENV !== 'production';
          if (isDev) {
            console.log('🔌 Attempting WebSocket connection to:', wsUrl);
          }
          ws = new WebSocket(wsUrl);
        } catch (createError) {
          // Log error in development/staging for debugging
          // @ts-ignore
          const isDev = typeof process !== 'undefined' && process.env && process.env.NODE_ENV !== 'production';
          if (isDev) {
            console.error('❌ Failed to create WebSocket:', createError);
          }
          isConnectingRef.current = false;
          return;
        }
        
        wsRef.current = ws;

        // Wrap all handlers in try-catch to prevent errors
        ws.onopen = () => {
          try {
            isConnectingRef.current = false;
            setConnectionStatus('connected');
            setIsConnected(true);
            
            // @ts-ignore
            const isDev = typeof process !== 'undefined' && process.env && process.env.NODE_ENV !== 'production';
            if (isDev) {
              console.log('✅ WebSocket connected successfully');
            }
            
            // Authenticate the connection
            try {
              if (user?.user?.id && user?.user?.role) {
                ws.send(JSON.stringify({
                  type: 'auth',
                  userId: user.user.id,
                  userRole: user.user.role
                }));
                if (isDev) {
                  console.log('📤 Sent WebSocket auth message');
                }
              }
            } catch (sendError) {
              if (isDev) {
                console.error('❌ Failed to send auth message:', sendError);
              }
            }
          } catch (error) {
            // @ts-ignore
            const isDev = typeof process !== 'undefined' && process.env && process.env.NODE_ENV !== 'production';
            if (isDev) {
              console.error('❌ WebSocket onopen error:', error);
            }
          }
        };

        ws.onmessage = (event) => {
          try {
            const message: WebSocketMessage = JSON.parse(event.data);
            
            switch (message.type) {
              case 'auth_success':
                break;
              case 'notification':
                try {
                  handleNewNotification(message.data);
                } catch (notifError) {
                  // Silently fail
                }
                break;
            }
          } catch (error) {
            // Silently fail - don't log errors
          }
        };

        ws.onclose = (event) => {
          try {
            isConnectingRef.current = false;
            setIsConnected(false);
            setConnectionStatus('disconnected');
            wsRef.current = null;
            
            // Don't auto-reconnect - it causes issues
            if (reconnectTimeoutRef.current) {
              clearTimeout(reconnectTimeoutRef.current);
              reconnectTimeoutRef.current = undefined;
            }
          } catch (error) {
            // Silently fail
          }
        };

        ws.onerror = (error) => {
          try {
            // Log error in development/staging for debugging
            // @ts-ignore
            const isDev = typeof process !== 'undefined' && process.env && process.env.NODE_ENV !== 'production';
            if (isDev) {
              console.error('❌ WebSocket error:', {
                url: wsUrl,
                readyState: wsRef.current?.readyState,
                error: error,
                currentHost: window.location.host,
                apiBaseUrl: apiBaseUrl
              });
              console.error('   Possible causes:');
              console.error('   1. Reverse proxy (nginx) not configured for WebSocket upgrades');
              console.error('   2. WebSocket server not accessible at this URL');
              console.error('   3. Network/firewall blocking WebSocket connections');
              console.error('   4. SSL/TLS certificate issues (for wss://)');
            }
            
            isConnectingRef.current = false;
            setConnectionStatus('disconnected');
            setIsConnected(false);
            
            // Close the connection immediately to prevent retries
            if (wsRef.current) {
              try {
                wsRef.current.close();
              } catch (e) {
                // Ignore
              }
              wsRef.current = null;
            }
          } catch (e) {
            // Ignore all errors
          }
        };

      } catch (innerError) {
        // Silently fail
        isConnectingRef.current = false;
      }

    } catch (error) {
      // Completely silent - don't break anything
      isConnectingRef.current = false;
    }
  }, [user?.user?.id, user?.user?.role]);

  const disconnect = useCallback(() => {
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
    }
    
    if (wsRef.current) {
      console.log('🔌 Manually disconnecting WebSocket');
      wsRef.current.close(1000, 'Manual disconnect');
      wsRef.current = null;
    }
    
    setIsConnected(false);
    setConnectionStatus('disconnected');
  }, []);

  const handleNewNotification = useCallback((notificationData: NotificationData) => {
    console.log('🔔 Processing new notification:', notificationData);
    
    // Invalidate and refetch notification queries to update UI
    queryClient.invalidateQueries({ queryKey: ['/notifications/unread'] });
    queryClient.invalidateQueries({ queryKey: ['/notifications/category'] });
    
    // Show browser notification if permission is granted
    if ('Notification' in window && Notification.permission === 'granted') {
      new Notification(notificationData.title, {
        body: notificationData.message,
        icon: '/favicon.ico',
        badge: '/favicon.ico'
      });
    }
    
    // Trigger custom event for other components to listen to
    window.dispatchEvent(new CustomEvent('newNotification', { 
      detail: notificationData 
    }));
  }, []);

  // Auto-connect when user is available - but only once, no retries
  useEffect(() => {
    // Only try to connect once, don't retry on errors
    if (user?.user?.id && connectionStatus === 'disconnected' && !isConnectingRef.current) {
      // Delay connection slightly to avoid blocking other features
      const timeoutId = setTimeout(() => {
        try {
          connect();
        } catch (e) {
          // Silently fail
        }
      }, 1000); // 1 second delay
      
      return () => {
        clearTimeout(timeoutId);
        if (reconnectTimeoutRef.current) {
          clearTimeout(reconnectTimeoutRef.current);
        }
      };
    }
    
    return () => {
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
      }
    };
  }, [user?.user?.id, connect, connectionStatus]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      disconnect();
    };
  }, [disconnect]);

  // Request notification permissions
  useEffect(() => {
    if ('Notification' in window && Notification.permission === 'default') {
      Notification.requestPermission().then(permission => {
        console.log('🔔 Notification permission:', permission);
      });
    }
  }, []);

  return {
    isConnected,
    connectionStatus,
    connect,
    disconnect
  };
}